import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { useSearch } from '@/hooks/useSearch';

interface SelectedBookingData {
  bookingId: string;
  bookingData?: any;
}

interface SearchContextType {
  isCommandPaletteOpen: boolean;
  setIsCommandPaletteOpen: (open: boolean) => void;
  query: string;
  setQuery: (query: string) => void;
  results: any[];
  loading: boolean;
  recentSearches: string[];
  addRecentSearch: (search: string) => void;
  clearRecentSearches: () => void;
  // Новый callback для открытия модала бронирования
  selectedBookingFromSearch: SelectedBookingData | null;
  selectBookingFromSearch: (bookingId: string, bookingData?: any) => void;
  clearSelectedBooking: () => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const useSearchContext = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearchContext must be used within SearchProvider');
  }
  return context;
};

interface SearchProviderProps {
  children: ReactNode;
}

export const SearchProvider = ({ children }: SearchProviderProps) => {
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('admin-recent-searches') || '[]');
    } catch {
      return [];
    }
  });
  const [selectedBookingFromSearch, setSelectedBookingFromSearch] = useState<SelectedBookingData | null>(null);
  
  const { query, setQuery, results, loading } = useSearch();
  
  // Debounce console logging to prevent spam
  const resultsRef = React.useRef(results.length);
  React.useEffect(() => {
    if (resultsRef.current !== results.length) {
      console.log('🔍 SearchProvider - текущие результаты:', results.length);
      resultsRef.current = results.length;
    }
  }, [results.length]);

  const addRecentSearch = React.useCallback((search: string) => {
    if (!search.trim()) return;
    
    setRecentSearches(prev => {
      const updated = [search, ...prev.filter(s => s !== search)].slice(0, 5);
      try {
        localStorage.setItem('admin-recent-searches', JSON.stringify(updated));
      } catch (error) {
        console.warn('Failed to save recent searches:', error);
      }
      return updated;
    });
  }, []);

  const clearRecentSearches = React.useCallback(() => {
    setRecentSearches([]);
    try {
      localStorage.removeItem('admin-recent-searches');
    } catch (error) {
      console.warn('Failed to clear recent searches:', error);
    }
  }, []);

  const selectBookingFromSearch = useCallback((bookingId: string, bookingData?: any) => {
    console.log('🔍 Выбрана бронь из поиска:', bookingId);
    setSelectedBookingFromSearch({ bookingId, bookingData });
    setIsCommandPaletteOpen(false);
    setQuery('');
  }, [setQuery]);

  const clearSelectedBooking = useCallback(() => {
    setSelectedBookingFromSearch(null);
  }, []);

  const contextValue = React.useMemo(() => ({
    isCommandPaletteOpen,
    setIsCommandPaletteOpen,
    query,
    setQuery,
    results,
    loading,
    recentSearches,
    addRecentSearch,
    clearRecentSearches,
    selectedBookingFromSearch,
    selectBookingFromSearch,
    clearSelectedBooking
  }), [
    isCommandPaletteOpen,
    query,
    results,
    loading,
    recentSearches,
    addRecentSearch,
    clearRecentSearches,
    selectedBookingFromSearch,
    selectBookingFromSearch,
    clearSelectedBooking
  ]);

  return (
    <SearchContext.Provider value={contextValue}>
      {children}
    </SearchContext.Provider>
  );
};